import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import {
	setComplianceSupplierStatuses,
	setComplianceClientStatuses,
} from '../reducers/compliance_party_statuses_reducer';

const fetchUtils = new FetchUtils();

const JSON_HEADERS = { Accept: 'application/json', 'Content-Type': 'application/json' };

// Party-level compliance status endpoints (proposed backend contract):
//   GET  /api/v1/{buyer|supplier}/compliance/supplier_statuses
//   PUT  /api/v1/{buyer|supplier}/compliance/supplier_statuses
//   GET  /api/v1/supplier/compliance/client_statuses
//
// These drive the per-row Compliant / Non-Compliant badges on the overview
// tabs — the backend owns the non-compliance rule, so the client no longer
// derives it. There is no reducer slice: the overview pages hold the resolved
// list in local state, so (like the custom fetchComplianceSummary thunk) these
// simply resolve the parsed envelope `.data`.

const resolveEnvelope = (dispatch, requestPromise) =>
	new Promise((resolve, reject) => {
		requestPromise
			.then(parseJSON)
			.then(({ json, status }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
					reject('unauthorized');
					return;
				}
				if (json.status === 'error') {
					reject(json.message);
					return;
				}
				resolve(json.data);
			})
			.catch((error) => reject(error && error.message));
	});

// GET supplier_statuses -> [{ supplierFacilityId, supplierFacilityName,
// isCompliant, canReceiveWorkOrders, allowWorkOrdersWhenNonCompliant }]
export const fetchComplianceSupplierStatuses = (userType) => () => (dispatch) =>
	resolveEnvelope(
		dispatch,
		fetchUtils.get(`${getBackendUri()}/api/v1/${userType}/compliance/supplier_statuses`, {
			headers: JSON_HEADERS,
		})
	).then((supplierStatuses: any[]) => {
		// Store to redux so the Overview badges re-render, and return the list for
		// callers that resolve it directly (e.g. legacy local-state consumers).
		dispatch(setComplianceSupplierStatuses(supplierStatuses));
		return supplierStatuses;
	});

// GET client_statuses -> [{ buyerCompanyId, buyerCompanyName, isCompliant,
// canReceiveWorkOrders }]
export const fetchComplianceClientStatuses = (userType) => () => (dispatch) =>
	resolveEnvelope(
		dispatch,
		fetchUtils.get(`${getBackendUri()}/api/v1/${userType}/compliance/client_statuses`, {
			headers: JSON_HEADERS,
		})
	).then((clientStatuses: any[]) => {
		dispatch(setComplianceClientStatuses(clientStatuses));
		return clientStatuses;
	});

// PUT supplier_statuses body { supplierFacilityId, allowWorkOrdersWhenNonCompliant:
// true | false | null } -> refreshed supplier_statuses list.
export const updateComplianceSupplierWorkOrderOverride = (userType) => (body) => (dispatch) =>
	resolveEnvelope(
		dispatch,
		fetchUtils.put(`${getBackendUri()}/api/v1/${userType}/compliance/supplier_statuses`, {
			headers: JSON_HEADERS,
			data: body,
		})
	).then((supplierStatuses: any[]) => {
		// The PUT returns the refreshed list — store it so the badge/label update.
		dispatch(setComplianceSupplierStatuses(supplierStatuses));
		return supplierStatuses;
	});
